import {
    Box,
    Button,
    Dialog,
    Hidden,
    IconButton,
    Theme,
    Typography
} from "@mui/material";
import {
    navigationData,
    PaginationObj
} from "../types";
import { Redirect, useHistory } from "react-router";
import {
    useDispatch,
    useSelector
} from "react-redux";
import { RootStateType } from "../../../redux-store/reducers";
import BreadCrum from "../../../lib/BreadCrum";
import {
    getInvestorInfo,
    setAppliedFundsFilters,
    setAppliedHoldingsFilters,
    setAppliedSystematicPlansFilters,
    setCheckedHoldingSchemes,
    setExploreMutualFundsPageVisited,
    setHoldingsActiveCategory,
    setSelectedAllHoldingsInfo,
    setSelectedHoldingsIds,
    setSelectedHoldingsInfo,
    setSelectedSchemesObj,
    setSystematicPlansSearchText
} from "../../../redux-store/actions/investorProfile";
import StmtForm from "./StmtForm";
import StmtTable from "./StmtTable";
import {
    SmallScreenStmtSelection,
    StmtSelection
} from "./StmtSelection";
import {
    useEffect,
    useRef,
    useState
} from "react";
import {
    CASSuccessIcon,
    DialogCloseIcon,
    InfoIconFilled,
    RefreshIcon
} from "../../../assets/SvgIcons";
import {
    Form,
    Formik,
    FormikProps
} from "formik";
import dayjs from "dayjs";
import OTPForm from "../../../lib/formik/OtpForm";
import {
    GetReportListEcasResponseBody,
    ReportListEcas,
    SendOtpEcasResponseBody,
    VerifyOtpEcasResponseBody
} from "../../../redux-store/types/eCasDownloads";
import {
    ecasDownload,
    getReportListEcas,
    sendOtpEcas,
    validateMFCQR,
    verifyOtpEcas
} from "../../../redux-store/actions/eCasDownloads";
import { showError } from "../../../redux-store/actions/auth";
import { reportDownload } from "../../Reports/report-utils";
import { useSnackbar } from "notistack";
import * as Yup from 'yup';
import {
    currentDate,
    currentFinancialYear,
    statementTitleMap,
    Values
} from "./stmtCommon";
import {
    isRMLikeRole,
    maskEmailId,
    maskInfo,
    maskMobileNumber
} from "../../../utils/utilityFunctions";
import { CONTENT_TYPE, ECAS_INFO, USER_ROLES } from "../../../utils/constant";
import { InvestorInfoRes } from "../../../redux-store/types/investorProfile";
import { storePortfolioList } from "../../../redux-store/actions/portfolioFilter";
import { Notes } from "../../investors/components";
import useQuery from "../../../utils/useQuery";
import MFCPopup from "./MFCPopup";

const validationSchema = Yup.object().shape({
    frequency: Yup
        .string(),
    fromDate: Yup
        .date()
        .nullable()
        .typeError('From Date should be a valid date')
        .max(currentDate, "From Date cannot be greater than today's date")
        .test("from-date-validation", function (value, context) {
            const { parent, createError, path } = context;
            const { frequency, toDate } = parent

            if (frequency === "specificPeriod") {
                if (!value) {
                    return createError({ path, message: 'From Date is required' })
                }

                if (dayjs(value).isAfter(toDate)) {
                    return createError({ path, message: 'From Date cannot be greater than To Date' })
                }
            }
            return true
        }),
    toDate: Yup
        .date()
        .nullable()
        .typeError('To Date should be valid date')
        .max(currentDate, "To Date cannot be greater than today's date")
        .test("to-date-validation", function (value, context) {
            const { parent, createError, path } = context;
            const { frequency, fromDate } = parent

            if (!value) {
                return createError({ path, message: 'To Date is required' })
            }

            if (frequency === "specificPeriod") {
                if (dayjs(value).isBefore(fromDate)) {
                    return createError({ path, message: 'To Date cannot be less than From Date' })
                }
            }
            return true
        }),
})

const initialValues: Values = {
    frequency: "asOnDate",
    fromDate: dayjs(new Date(1990, 0, 1)),
    toDate: dayjs(currentDate)
}

export default function EcasDownloads(): JSX.Element {
    const dispatch = useDispatch()

    const history = useHistory()
    const { pathname: pathName = "", state } = history.location
    const navState = state as navigationData

    const { enqueueSnackbar } = useSnackbar();

    const formikRef = useRef<FormikProps<Values>>(null);

    const { selectedPortfolio, selectedPortfolioObj = {} } = useSelector((store: RootStateType) => store.selectedPortfiolio);
    const { role = "" } = useSelector((store: RootStateType) => store.auth);
    const { familyMemberId = "" } = useSelector((store: RootStateType) => store.id);
    const {
        panNo: applicantPanNo = "",
        email: applicantEmailId = "",
        mobile: applicantMobileNumber = "",
        status: applicantStatus = "",
        countryCode: applicantCountryCode = "",
        isPortfolioAvailable = false,
        applicationId,
        iwappRmId,
        investorClientId = ""
    } = useSelector((store: RootStateType) => store.id);

    const query = useQuery()

    const [isPopupOpen, setPopupOpen] = useState<boolean>(['success', 'error'].includes(query.get('status') ?? ''));
    const mfcReqId = query.get('reqId');
    const initialRouter = pathName.split('/').filter((item) => item !== '')
    const rmRouterPath = [
        {
            name: 'Dashboard',
            path: '/dashboard',
        },
        {
            name: initialRouter[1] + `${selectedPortfolio.label ? `(${selectedPortfolio.label})` : ''}`,
            path: `/${initialRouter[0]}/${initialRouter[1]}`,
            state: { clientCode: navState?.clientCode ?? "" }
        },
        {
            name: 'Detailed Portfolio Holdings',
            path: `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}`,
            state: { clientCode: navState?.clientCode ?? "" },
            search: `tab=Overview&portfolio=${selectedPortfolio?.value ?? ""}`
        },
        {
            name: 'eCAS Reports',
            path: `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}/${initialRouter[3]}`,
            state: { clientCode: navState?.clientCode ?? "" },
            isTextLowerCase: false,
            textSx: { textTransform: 'none' }
        },
    ]

    const investorRouterPath = [
        ...(familyMemberId
            ?
            [{
                name: 'View Family',
                path: `/investor-family/family-profile/${familyMemberId}`,
            },
            {
                name: `${initialRouter[1]}`,
                path: `/${initialRouter[0]}/${initialRouter[1]}`,
            },
            {
                name: 'Detailed Portfolio Holdings',
                path: `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}`,
                state: {
                    clientCode: navState?.clientCode ?? ""
                },
                search: `tab=Overview&portfolio=${selectedPortfolio?.value ?? ""}`
            },
            {
                name: 'eCAS Reports',
                path: `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}/${initialRouter[3]}`,
                state: { clientCode: navState?.clientCode ?? "" },
                isTextLowerCase: false,
                textSx: { textTransform: 'none' }
            }]
            :
            [{
                name: 'Dashboard' + `${selectedPortfolio.label ? `(${selectedPortfolio.label})` : ''}`,
                path: '/investor-dashboard',
            },
            {
                name: 'Detailed Portfolio Holdings',
                path: `/${initialRouter[0]}/${initialRouter[1]}`,
                state: { clientCode: navState?.clientCode ?? "" },
                search: `tab=Overview&portfolio=${selectedPortfolio?.value ?? ""}`
            },
            {
                name: 'eCAS Reports',
                path: `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}`,
                state: { clientCode: navState?.clientCode ?? "" },
                isTextLowerCase: false,
                textSx: { textTransform: 'none' }
            }]
        ),
    ]

    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const [isTableDataLoading, setTableDataLoading] = useState<boolean>(false)
    const [isFormSubmitting, setFormSubmitting] = useState<boolean>(false)
    const [selectedStmt, setSelectedStmt] = useState<string>("detailedAccountStmt")
    const [isOtpDialogOpen, setOtpDialogOpen] = useState<boolean>(false)
    const [isSuccessDialogOpen, setSuccessDialogOpen] = useState<boolean>(false)
    const [tableData, setTableData] = useState<Array<ReportListEcas>>([])
    const [tablePaginationObj, setTablePaginationObj] = useState<PaginationObj>({
        currentPage: 0,
        rowsPerPage: 5,
        totalRows: 0
    })
    const [selectedIdForDownload, setSelectedIdForDownload] = useState<string | null>(null)
    const [sendOtpData, setSendOtpData] = useState<SendOtpEcasResponseBody>({} as SendOtpEcasResponseBody)
    const [infoGraphicsOpen, setInfoGraphicsOpen] = useState<boolean>(false);

    const fetchTableData = async ({
        currentPage,
        rowsPerPage,
    }: {
        currentPage: number;
        rowsPerPage: number;
    }) => {
        const payload = {
            pan: applicantPanNo,
            clientCode: `${investorClientId}`,
            currentPage,
            rowsPerPage
        }

        const response = await (dispatch(getReportListEcas(payload)) as unknown as Promise<GetReportListEcasResponseBody>)

        if (!response) {
            return
        }

        if (!response?.isSuccess) {
            dispatch(showError(response?.message ?? ""))
            return
        }

        if (response?.isSuccess) {
            setTableData(response?.result?.docsRequestList ?? [])
            setTablePaginationObj({
                currentPage: currentPage - 1,
                rowsPerPage,
                totalRows: response?.result?.count ?? 0
            })

            return
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                setTableDataLoading(true)
                await fetchTableData({
                    currentPage: 1,
                    rowsPerPage: 5,
                })
            } catch (e) {
                console.log("Error:", e)
            } finally {
                setTableDataLoading(false)
            }
        }

        fetchData()

    }, [])

    const handleExploreMutualFunds = () => {

        dispatch(setSelectedAllHoldingsInfo([]))

        dispatch(setAppliedFundsFilters([]));
        dispatch(setSelectedSchemesObj({}));
        dispatch(setExploreMutualFundsPageVisited(false));

        dispatch(setAppliedHoldingsFilters([]))
        dispatch(setHoldingsActiveCategory("All"))
        dispatch(setSelectedHoldingsIds([]))
        dispatch(setCheckedHoldingSchemes([]))
        dispatch(setSelectedHoldingsInfo([]))

        dispatch(setSystematicPlansSearchText(""))
        dispatch(setAppliedSystematicPlansFilters([]))

        const modifiedPathName = pathName.replace("/ecas-reports", "")

        history.push(`${modifiedPathName}/explore`, {
            clientCode: navState?.clientCode || "",
        })
    }

    const handleStmtSelection = (stmt: string) => {
        setSelectedStmt(stmt)
    }

    const handleRefresh = async () => {
        try {
            setTableDataLoading(true)
            await fetchTableData({
                currentPage: 1,
                rowsPerPage: 5,
            })
        } catch (e) {
            console.log("Error:", e)
        } finally {
            setTableDataLoading(false)
        }
    }

    const handleTableRowsPerPageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        try {
            setTableDataLoading(true)

            await fetchTableData({
                currentPage: 1,
                rowsPerPage: Number(event.target.value)
            })
        } catch (e) {
            console.log("Error:", e)
        } finally {
            setTableDataLoading(false)
        }
    }

    const handleTablePageChange = async (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        try {
            setTableDataLoading(true)

            await fetchTableData({
                currentPage: page + 1,
                rowsPerPage: tablePaginationObj.rowsPerPage
            })
        } catch (e) {
            console.log("Error:", e)
        } finally {
            setTableDataLoading(false)
        }
    }

    const handleStatementDownload = async (selectedReqId: string) => {
        try {
            setIsDownloading(true)
            setSelectedIdForDownload(selectedReqId)

            const payload = { reqId: `${selectedReqId}` }

            const response = await (dispatch(ecasDownload(payload)) as unknown as Promise<any>)

            if (response.type === CONTENT_TYPE.EXCEL) {
                enqueueSnackbar('File downloaded successfully.', {
                    variant: 'success',
                    autoHideDuration: 5000,
                });
                reportDownload(response as unknown as Blob, `${applicantPanNo ?? ""}-${statementTitleMap[selectedStmt]}.xlsx`);
            } else {
                enqueueSnackbar('File download failed!.', {
                    variant: 'error',
                    autoHideDuration: 5000,
                });
            }

        } catch (e) {
            console.log("Error:", e)
        } finally {
            setIsDownloading(false)
            setSelectedIdForDownload(null)
        }
    }

    const handleMFActionIconClick = async (selectedReqId: string) => {
        try {
            setIsDownloading(true)
            setSelectedIdForDownload(selectedReqId)

            const payload = {
                // clientCode: navState?.clientCode ?? "",
                clientCode: `${investorClientId}`,
                portfolio: selectedPortfolio?.value,
                applicationId: applicationId,
                rmId: `${iwappRmId}`,
            }

            const response = await (dispatch(getInvestorInfo(payload)) as unknown as Promise<InvestorInfoRes>)

            if (response.isSuccess) {

                const modifiedResponse = {
                    ...response,
                    name: maskInfo(response?.name),
                    emailId: maskInfo(response?.emailId),
                    mobileNumber: maskInfo(response?.mobileNumber),
                }

                const portfolioListArr = modifiedResponse?.portfolioList?.map((portfolio) => {
                    return (
                        {
                            ...portfolio,
                            label: maskInfo(portfolio?.portfolioName),
                            value: portfolio?.portfolioId
                        }
                    )
                }) ?? [{
                    label: '',
                    value: ''
                }]

                dispatch(storePortfolioList(portfolioListArr))

                const heldAwayPortfolioObj = (portfolioListArr ?? []).find(obj => !!obj?.isPortfolioHeldaway)

                const modifiedPathName = pathName.replace("/ecas-reports", "")

                if (heldAwayPortfolioObj) {
                    history.push(`${modifiedPathName}?tab=Overview&portfolio=${heldAwayPortfolioObj?.portfolioId ?? ""}`, {
                        clientCode: navState?.clientCode || "",
                    })
                }
            }
        } catch (e) {
            console.log("Error:", e)
        } finally {
            setIsDownloading(false)
            setSelectedIdForDownload(null)
        }
    }

    const onOtpDialogClose = () => {
        setOtpDialogOpen(false)
    }

    const onSuccessDialogClose = () => {
        setSuccessDialogOpen(false)
    }
    const fetchSendOtp = async ({
        fromDate,
        toDate
    }: Omit<Values, "frequency">) => {
        const payload = {
            pan: applicantPanNo,
            email: applicantEmailId,
            // email: "",
            mobile: applicantMobileNumber,
            toDate: toDate.format("DD-MMM-YYYY"),
            fromDate: fromDate.format("DD-MMM-YYYY"),
            status: applicantStatus,
            countryCode: applicantCountryCode,
            redirectUrl: isRMLikeRole(role) ? `${window.location.origin}/investor-profile/${selectedPortfolioObj?.portfolioName}/mutual-fund/ecas-reports` : `${window.location.origin}/investor-dashboard/mutual-fund/ecas-reports`,
            clientCode: investorClientId
        }

        return await (dispatch(sendOtpEcas(payload)) as unknown as Promise<SendOtpEcasResponseBody>)
    }

    const handleRequestStatement = async () => {
        setInfoGraphicsOpen(true);

    }

    const onInfoGraphicsClose = () => {
        setInfoGraphicsOpen(false);
    }
    const handleStmtPostInfo = async () => {
        try {
            onInfoGraphicsClose();
            if (!formikRef.current) {
                return
            }

            const {
                fromDate,
                toDate
            } = formikRef.current.values


            setFormSubmitting(true)

            const response = await fetchSendOtp({
                fromDate,
                toDate
            })

            if (!response) {
                return
            }

            // if (!response?.isSuccess) {
            //     dispatch(showError(response?.message ?? ""))
            //     return
            // }

            if (response?.mfc_redirect_url) {
                // setSendOtpData(response)
                // setOtpDialogOpen(true)
                // setPopupOpen(true);
                window.location.href = response?.mfc_redirect_url
            }
        } catch (e) {
            console.log("Error:", e);
        } finally {
            setFormSubmitting(false)
        }
    }

    const handleOtpResend = async () => {
        if (!formikRef.current) {
            return
        }

        const {
            fromDate,
            toDate
        } = formikRef.current.values

        const response = await fetchSendOtp({
            fromDate,
            toDate
        })

        if (!response) {
            return
        }

        if (!response?.isSuccess) {
            dispatch(showError(response?.message ?? ""))
            return
        }

        if (response?.isSuccess) {
            setSendOtpData(response)
        }
    }

    const handleOtpSubmit = async (otp: string) => {
        const payload = {
            pan: applicantPanNo ?? "",
            enteredOTP: otp,
            reqId: sendOtpData?.result?.reqId ?? ""
        }

        const response = await (dispatch(verifyOtpEcas(payload)) as unknown as Promise<VerifyOtpEcasResponseBody>)

        if (!response) {
            return
        }

        if (!response.isSuccess) {
            dispatch(showError(response?.message ?? ""))
            return
        }

        if (response.isSuccess) {
            setOtpDialogOpen(false)
            setSuccessDialogOpen(true)
            await handleRefresh()

            return
        }
    }

    const isStatusNRI = ['nre', 'nro'].includes((applicantStatus ?? "")?.toLowerCase());

    const handlePopupClose = () => {
        history.replace({
            pathname: isRMLikeRole(role) ? `/investor-profile/${selectedPortfolioObj?.portfolioName}/mutual-fund/ecas-reports` : `/investor-dashboard/mutual-fund/ecas-reports`,
            state: { clientCode: investorClientId ?? "" }
        })
        setPopupOpen(false);
    }

    const uploadQR = async (base64File: string) => {
        const payload = {
            reqId: mfcReqId ?? "",
            qrCode: base64File
        }

        const response = await (dispatch(validateMFCQR(payload)) as unknown as Promise<any>);
        if (!response) return;
        if (response?.isSuccess) {
            handlePopupClose();
            setSuccessDialogOpen(true)
            await handleRefresh()
            return;
        }
        else if (response && !response?.isSuccess) {
            handlePopupClose();
            dispatch(showError(response?.message ?? ""));
            return;
        }
    }

    const steps = [
        {
            title: "Enter Your OTP on MFC",
            description:
                "An OTP will be sent to your registered mobile/email. Enter it on the MFC screen to authenticate."
        },
        {
            title: "Choose Type of Data & Generate QR",
            description:
                "Select investment type, choose extent of data, select AMCs and generate the QR code.",
            note: `In Extent of Data section the "Summary of Holding" option isn't working currently. Please choose the "Transaction" option instead.`
        },
        {
            title: "Download the QR Code",
            description:
                "Once the QR is generated, download the QR code to your device.",
            note: `Copy QR option isn't working currently, please download the QR instead.`
        },
        {
            title: 'Click "Continue" on MFC',
            description:
                "After downloading, click Continue on the MFC page to return back to the portal."
        },
        {
            title: "Upload the QR Code",
            description:
                "Upload the QR image you downloaded in Step 3."
        },
        {
            title: "You're All Set! 🎉",
            description:
                "A success popup will confirm the upload. Your mutual fund data will appear in the External Portfolio section."
        }
    ];



    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    rowGap: "25px"
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: "nowrap",
                        justifyContent: 'space-between',
                        alignItems: "center"
                    }}
                >
                    <BreadCrum array={isRMLikeRole(role) ? rmRouterPath : investorRouterPath} />
                    <Button
                        onClick={handleExploreMutualFunds}
                        variant="contained"
                        sx={(theme: Theme) => ({
                            ml: { xs: "auto" },
                            ...theme.typography.subtitle_300,
                            fontWeight: 700,
                            borderRadius: "10px",
                            padding: "10px 15px",
                            backgroundColor: theme.palette.primary[500],
                            color: theme.palette.textDark.primary,
                            "&.Mui-disabled": {
                                backgroundColor: `${theme.palette.text.secondary}B2`,
                                color: theme.palette.text.secondary,
                            },
                        })}
                        disabled={[USER_ROLES.INVESTOR].includes(role) && !isPortfolioAvailable}
                    >
                        Explore <Hidden only='xs'> Mutual Funds</Hidden>
                    </Button>
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        display: {
                            xs: "block",
                            md: "none"
                        },
                        height: "100%",
                        position: "sticky",
                        top: 0,
                        zIndex: 99
                    }}
                >
                    <SmallScreenStmtSelection
                        selectedStmt={selectedStmt}
                        handleStmtSelection={handleStmtSelection}
                        isDownloading={isDownloading}
                    />
                </Box>
                <Box
                    sx={(theme: Theme) => ({
                        display: "flex",
                        columnGap: "15px",
                        flexWrap: "nowrap",
                        borderRadius: "25px",
                        padding: { xs: "15px", sm: "35px" },
                        backgroundColor: theme.palette.common.white,
                    })}
                >
                    <Box
                        sx={{
                            flex: 1,
                            display: {
                                xs: "none",
                                md: "block"
                            }
                        }}
                    >
                        <StmtSelection
                            selectedStmt={selectedStmt}
                            handleStmtSelection={handleStmtSelection}
                            isDownloading={isDownloading}
                        />
                    </Box>
                    <Box
                        sx={(theme: Theme) => ({
                            flex: { xs: 1, md: 2 },
                            border: `1px solid ${theme.palette.text.secondary}26`,
                            borderRadius: "26px",
                            padding: { xs: "15px", sm: "35px" },
                            display: "flex",
                            flexDirection: "column"
                        })}
                    >
                        <Notes icon={<InfoIconFilled />} displayContent={ECAS_INFO}
                        />
                        <Box
                            sx={{
                                mt: 2
                            }}
                        >
                            <Formik
                                innerRef={formikRef}
                                validationSchema={validationSchema}
                                initialValues={initialValues}
                                onSubmit={handleRequestStatement}
                            >
                                {() => (
                                    <Form>
                                        <StmtForm
                                            key={selectedStmt}
                                            selectedStmt={selectedStmt}
                                            isFormSubmitting={isFormSubmitting}
                                            isDownloading={isDownloading}
                                        />
                                    </Form>
                                )}
                            </Formik>
                        </Box>
                        <Box
                            sx={{
                                paddingTop: "30px",
                                display: "flex",
                                flexDirection: "column",
                                rowGap: "30px"
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    columnGap: "20px"
                                }}
                            >
                                <Typography
                                    variant='h2'
                                    sx={(theme: Theme) => ({
                                        color: theme.palette.text.primary,
                                        letterSpacing: "0.01px"
                                    })}
                                >
                                    Transaction History
                                </Typography>
                                <IconButton
                                    aria-label="Refresh data"
                                    sx={{
                                        cursor: 'pointer',
                                        "&: disabled": {
                                            opacity: 0.5
                                        }
                                    }}
                                    onClick={handleRefresh}
                                    disabled={isTableDataLoading}
                                >
                                    <RefreshIcon />
                                </IconButton>
                            </Box>
                            <StmtTable
                                isLoading={isTableDataLoading}
                                tableData={tableData}
                                tablePaginationObj={tablePaginationObj}
                                handleTableRowsPerPageChange={handleTableRowsPerPageChange}
                                handleTablePageChange={handleTablePageChange}
                                handleStatementDownload={handleStatementDownload}
                                isDownloading={isDownloading}
                                selectedIdForDownload={selectedIdForDownload}
                                handleMFActionIconClick={handleMFActionIconClick}
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* OTP DIALOG START */}
            <Dialog
                open={isOtpDialogOpen}
                onClose={onOtpDialogClose}
                maxWidth="sm"
                sx={(theme: Theme) => ({
                    '& .MuiPaper-root': {
                        borderRadius: '12px',
                        backgroundColor: theme.palette.common.white,
                        padding: "24px",
                        flexDirection: "column",
                        rowGap: "24px"
                    }
                })}
            >
                <Typography
                    component="span"
                    variant="body_500"
                    sx={(theme: Theme) => ({
                        lineHeight: "24px",
                        letterSpacing: "0.02px",
                        color: theme.palette.text.primary,
                        textAlign: "center"
                    })}
                >
                    {`An OTP has been sent to your ${isStatusNRI ? "Email Id" : "Mobile Number"}`}
                    <Typography
                        component="span"
                        variant="body_500"
                        sx={(theme: Theme) => ({
                            lineHeight: "24px",
                            letterSpacing: "0.02px",
                            color: theme.palette.primary[500]
                        })}
                    >
                        {" "}{isStatusNRI ? sendOtpData?.email : sendOtpData?.mobile}
                    </Typography>
                </Typography>
                <OTPForm
                    onSubmit={handleOtpSubmit}
                    onResend={handleOtpResend}
                    otpMessage=""
                    warningMessage=""
                />
            </Dialog>
            {/* OTP DIALOG END */}

            {/* SUCCESS DIALOG START */}
            <Dialog
                onClose={onSuccessDialogClose}
                open={isSuccessDialogOpen}
                maxWidth='sm'
                sx={(theme: Theme) => ({
                    '& .MuiPaper-root': {
                        borderRadius: '12px',
                        backgroundColor: theme.palette.common.white,
                        padding: "24px",
                        flexDirection: "column",
                    }
                })}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center"
                    }}
                >
                    <IconButton
                        aria-label="dialog-close"
                        onClick={onSuccessDialogClose}
                        sx={{ padding: "5px" }}
                    >
                        <DialogCloseIcon />
                    </IconButton>
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <CASSuccessIcon />
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        rowGap: "20px",
                        marginTop: "20px"
                    }}
                >
                    <Typography
                        component="span"
                        variant="body_500"
                        sx={(theme: Theme) => ({
                            fontWeight: 400,
                            lineHeight: "24px",
                            letterSpacing: "0.02px",
                            color: theme.palette.text.primary,
                            textAlign: "left"
                        })}
                    >
                        <Typography
                            component="span"
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                fontWeight: 400,
                                color: theme.palette.primary[500],
                                lineHeight: "24px",
                                letterSpacing: "0.02px",
                            })}
                        >
                            Note:
                        </Typography>
                        {/* {" "}Your Consolidated Account Statement (CAS) will be ready for download in approximately 30 minutes after you submit the request. */}
                        {" "}Your Consolidated Account Statement (CAS) will be ready in approximately 30 minutes after you submit the request.
                    </Typography>
                    <Typography
                        variant="body_500"
                        sx={(theme: Theme) => ({
                            fontWeight: 400,
                            lineHeight: "24px",
                            letterSpacing: "0.02px",
                            color: theme.palette.text.primary,
                            textAlign: "left"
                        })}
                    >
                        For a smooth experience, please ensure your primary mobile number is updated across all folios, as the CAS is generated based on your PAN and primary mobile number.
                    </Typography>
                    {/* <Typography
                        variant="body_500"
                        sx={(theme: Theme) => ({
                            fontWeight: 400,
                            lineHeight: "24px",
                            letterSpacing: "0.02px",
                            color: theme.palette.text.primary,
                            textAlign: "left"
                        })}
                    >
                        Once available, the statement can be downloaded from the Downloads section.
                    </Typography> */}
                </Box>
            </Dialog>
            {/* SUCCESS DIALOG END */}

            {/* MFC Popup */}

            <MFCPopup
                open={isPopupOpen}
                handleClose={handlePopupClose}
                onUpload={uploadQR}
                status={query.get('status') ?? "error"}
                showCloseButton
            />

            {/* Info Graphics Popup */}
            <Dialog
                onClose={onInfoGraphicsClose}
                open={infoGraphicsOpen}
                maxWidth='md'
                fullWidth
                sx={(theme) => ({
                    "& .MuiPaper-root": {
                        borderRadius: "12px",
                        backgroundColor: theme.palette.common.white,
                        padding: "20px",
                    }
                })}
            >
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="h2" mb={1}>
                        How to Import Your External Portfolio via MFC
                    </Typography>
                    <IconButton onClick={onInfoGraphicsClose}>
                        <DialogCloseIcon />
                    </IconButton>
                </Box>


                <Typography variant="body_400" color="text.secondary" mb={3}>
                    Follow the steps below to fetch your mutual fund data seamlessly.
                </Typography>

                {/* Steps */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

                    {steps.map((step, index) => (
                        <Box key={index}>

                            <Typography fontWeight={600}>
                                {index + 1}. {step.title}
                            </Typography>

                            <Typography variant="overline_400" color="text.secondary">
                                {step.description}
                            </Typography>

                            {step.note && (
                                <Box
                                    sx={(theme: Theme) => ({
                                        background: theme.palette.grey[50],
                                        padding: "10px",
                                        borderRadius: "8px",
                                        mt: 1
                                    })}
                                >
                                    <Typography variant="overline_400">
                                        Note: {step.note}
                                    </Typography>
                                </Box>
                            )}

                        </Box>
                    ))}

                </Box>

                {/* Proceed Button */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        mt: 4
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={handleStmtPostInfo}
                        sx={{
                            borderRadius: "8px",
                            padding: "10px 30px",
                            textTransform: "none"
                        }}
                    >
                        Proceed
                    </Button>
                </Box>
            </Dialog>
        </>
    )
}